import { videosService } from '@/api/videos.service';
import type {
  CreateVideoDto,
  UpdateVideoDto,
  Video,
  VideosControllerFindAllData,
} from '@carvia/ros-client-types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toRef, toValue } from 'vue';
import type { MutationDefaultsContext } from './query-client';
import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data';

const QUERY_KEY = 'videos';

export const useVideos = (
  query: MaybeRefOrGetter<VideosControllerFindAllData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY, query],
    queryFn: ({ pageParam = 1 }) =>
      videosService.getAllWithQuery({ ...toValue(query), page: pageParam }),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
    enabled: toRef(enabled),
  });
};

export const useVideo = (id: MaybeRefOrGetter<string | undefined | null>) => {
  return useQuery<Video>({
    queryKey: [QUERY_KEY, id],
    queryFn: () => videosService.getOne(toValue(id)!),
    enabled: toRef(!!id),
  });
};

export const useCreateVideo = () => {
  const queryClient = useQueryClient();
  return useMutation<Video, Error, CreateVideoDto>({
    mutationFn: (data) => videosService.create(data),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Video created',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      addItemToQueriesData<Video>([QUERY_KEY], result, queryClient);
    },
  });
};

export const useUpdateVideo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVideoDto }) =>
      videosService.update(id, data),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Video updated',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      updateItemOnQueriesData<Video>([QUERY_KEY], result, queryClient);
    },
  });
};

export const useDeleteVideo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => videosService.remove(id),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Video deleted',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      removeItemFromQueriesData<Video>([QUERY_KEY], variables, queryClient);
    },
  });
};
